import type { ReaderAiBlock, ReaderAiMessage, ReaderAiThread } from "@/types/agentic-chat";
import type { ToofiResumeTarget } from "@/types/student-toofi";
import type { ReaderAiPersistedState } from "@/lib/reader-ai-threads";
import type { ToofiArtifactRecordIndexItem } from "@/lib/toofi-artifact-types";

const getThreadMessages = (state: ReaderAiPersistedState, threadId: string) => state.messagesByThread[threadId] ?? [];

const getLatestThreadTimestamp = (thread: ReaderAiThread, messages: ReaderAiMessage[]) =>
  messages.length ? Math.max(thread.updatedAt, ...messages.map((message) => message.createdAt)) : thread.updatedAt;

const getLatestMatchingBlock = <TBlock extends ReaderAiBlock>(
  messages: ReaderAiMessage[],
  predicate: (block: ReaderAiBlock) => block is TBlock,
) => {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    for (let blockIndex = message.blocks.length - 1; blockIndex >= 0; blockIndex -= 1) {
      const block = message.blocks[blockIndex];
      if (predicate(block)) {
        return {
          block,
          message,
        };
      }
    }
  }
  return null;
};

const cleanResourceId = (resourceId: string | undefined) => (resourceId && resourceId !== "__workspace__" ? resourceId : null);
const buildResourceReaderPath = (resourceId: string | null) => (resourceId ? `/resource/${resourceId}?reader=1` : null);

type RankedThread = {
  thread: ReaderAiThread;
  messages: ReaderAiMessage[];
  updatedAt: number;
};

const rankThreads = (state: ReaderAiPersistedState) =>
  state.threads
    .filter((thread) => thread.scope === "workspace" && !thread.archived)
    .map((thread) => ({
      thread,
      messages: getThreadMessages(state, thread.id),
      updatedAt: getLatestThreadTimestamp(thread, getThreadMessages(state, thread.id)),
    }))
    .sort((left, right) => right.updatedAt - left.updatedAt);

const rankPinnedThreads = (rankedThreads: RankedThread[]) =>
  rankedThreads
    .filter((entry) => entry.thread.pinned)
    .sort((left, right) => right.updatedAt - left.updatedAt);

const rankWorkspaceArtifacts = (artifacts: ToofiArtifactRecordIndexItem[]) =>
  artifacts
    .filter((artifact) => artifact.scope === "workspace")
    .sort((left, right) => {
      if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
      return right.updatedAt - left.updatedAt;
    });

export const deriveToofiResumeTarget = (
  state: ReaderAiPersistedState,
  artifacts: ToofiArtifactRecordIndexItem[] = [],
): ToofiResumeTarget | null => {
  const rankedThreads = rankThreads(state);
  const rankedArtifacts = rankWorkspaceArtifacts(artifacts);
  if (!rankedThreads.length && !rankedArtifacts.length) return null;

  for (const entry of rankedThreads) {
    const latestGuidedStep = getLatestMatchingBlock(
      entry.messages,
      (block): block is Extract<ReaderAiBlock, { type: "guided_step" }> => block.type === "guided_step",
    );
    if (latestGuidedStep) {
      const isArabic = /[\u0600-\u06ff]/.test(`${latestGuidedStep.block.title} ${latestGuidedStep.block.content}`);
      const resourceId = cleanResourceId(entry.thread.resourceId);
      return {
        kind: "guided_lesson",
        threadId: entry.thread.id,
        resourceId,
        linkedPlanId: entry.thread.linkedPlanId ?? null,
        lessonId: latestGuidedStep.block.lessonId,
        label: isArabic ? "اكمل الدرس خطوة بخطوة" : "Resume step-by-step lesson",
        subtitle: latestGuidedStep.block.title,
        actionLabel: isArabic ? "اكمل الدرس" : "Resume lesson",
        sourceReason: "guided_lesson",
        deepLinkPath: buildResourceReaderPath(resourceId),
      };
    }
  }

  for (const entry of rankedThreads) {
    const latestEssay = getLatestMatchingBlock(
      entry.messages,
      (block): block is Extract<ReaderAiBlock, { type: "essay_workspace" }> => block.type === "essay_workspace",
    );
    if (latestEssay) {
      const resourceId = cleanResourceId(entry.thread.resourceId);
      return {
        kind: "essay",
        threadId: entry.thread.id,
        resourceId,
        linkedPlanId: entry.thread.linkedPlanId ?? null,
        label: "Resume your writing",
        subtitle: latestEssay.block.prompt,
        actionLabel: "Continue writing",
        sourceReason: "unfinished_essay",
        deepLinkPath: buildResourceReaderPath(resourceId),
      };
    }
  }

  const planArtifact = rankedArtifacts.find((artifact) =>
    artifact.kind === "plan_overview" ||
    artifact.kind === "plan_milestones" ||
    artifact.kind === "daily_plan" ||
    artifact.kind === "plan_checkin",
  );
  if (planArtifact) {
    return {
      kind: "artifact",
      artifactId: planArtifact.id,
      threadId: planArtifact.threadId,
      resourceId: cleanResourceId(planArtifact.resourceId ?? undefined),
      linkedPlanId: planArtifact.linkedPlanId ?? null,
      label: "Open your latest saved plan",
      subtitle: planArtifact.title,
      actionLabel: "Open artifact",
      sourceReason: "saved_artifact",
      deepLinkPath:
        planArtifact.sourcePagePath ?? buildResourceReaderPath(cleanResourceId(planArtifact.resourceId ?? undefined)),
    };
  }

  const reviewThread = rankedThreads.find((entry) => entry.thread.currentProductMode === "review");
  if (reviewThread) {
    const resourceId = cleanResourceId(reviewThread.thread.resourceId);
    return {
      kind: "review",
      threadId: reviewThread.thread.id,
      resourceId,
      linkedPlanId: reviewThread.thread.linkedPlanId ?? null,
      label: "Resume your review flow",
      subtitle: reviewThread.thread.title,
      actionLabel: "Continue review",
      sourceReason: "due_review",
      deepLinkPath: buildResourceReaderPath(resourceId),
    };
  }

  const planThread = rankedThreads.find(
    (entry) => entry.thread.linkedPlanId || entry.thread.currentProductMode === "plan",
  );
  if (planThread) {
    return {
      kind: "plan",
      threadId: planThread.thread.id,
      resourceId: cleanResourceId(planThread.thread.resourceId),
      linkedPlanId: planThread.thread.linkedPlanId ?? null,
      label: "Continue your plan with Toofi",
      subtitle: planThread.thread.title,
      actionLabel: "Resume planning",
      sourceReason: "plan_context",
      deepLinkPath: null,
    };
  }

  const pinnedThread = rankPinnedThreads(rankedThreads)[0];
  if (pinnedThread) {
    const resourceId = cleanResourceId(pinnedThread.thread.resourceId);
    return {
      kind: resourceId ? "resource_thread" : "workspace_thread",
      threadId: pinnedThread.thread.id,
      resourceId,
      linkedPlanId: pinnedThread.thread.linkedPlanId ?? null,
      label: "Return to your pinned Toofi chat",
      subtitle: pinnedThread.thread.title,
      actionLabel: "Open pinned chat",
      sourceReason: "pinned_thread",
      deepLinkPath: buildResourceReaderPath(resourceId),
    };
  }

  const savedArtifact = rankedArtifacts[0];
  if (savedArtifact) {
    return {
      kind: "artifact",
      artifactId: savedArtifact.id,
      threadId: savedArtifact.threadId,
      resourceId: cleanResourceId(savedArtifact.resourceId ?? undefined),
      linkedPlanId: savedArtifact.linkedPlanId ?? null,
      label: "Open your latest saved artifact",
      subtitle: savedArtifact.title,
      actionLabel: "Open artifact",
      sourceReason: "saved_artifact",
      deepLinkPath:
        savedArtifact.sourcePagePath ?? buildResourceReaderPath(cleanResourceId(savedArtifact.resourceId ?? undefined)),
    };
  }

  const resourceThread = rankedThreads.find((entry) => cleanResourceId(entry.thread.resourceId));
  if (resourceThread) {
    const resourceId = cleanResourceId(resourceThread.thread.resourceId);
    return {
      kind: "resource_thread",
      threadId: resourceThread.thread.id,
      resourceId,
      linkedPlanId: resourceThread.thread.linkedPlanId ?? null,
      label: "Resume your last resource thread",
      subtitle: resourceThread.thread.title,
      actionLabel: "Continue thread",
      sourceReason: "recent_resource",
      deepLinkPath: buildResourceReaderPath(resourceId),
    };
  }

  const workspaceThread = rankedThreads[0];
  return workspaceThread
    ? {
        kind: "workspace_thread",
        threadId: workspaceThread.thread.id,
        resourceId: cleanResourceId(workspaceThread.thread.resourceId),
        linkedPlanId: workspaceThread.thread.linkedPlanId ?? null,
        label: "Continue where you left off",
        subtitle: workspaceThread.thread.title,
        actionLabel: "Resume chat",
        sourceReason: "recent_thread",
        deepLinkPath: null,
      }
    : null;
};
